import { useEffect, useRef, useState } from "react";
import type { Table } from "@tanstack/react-table";
import type { ReactNode } from "react";

import { Icono } from "../Icono";
import { DataTableFilters } from "./DataTableFilters";
import type { DataTableBulkAction, DataTableFilter } from "./types";

/**
 * La barra de encima de la tabla: buscar, filtrar, elegir columnas y actuar
 * sobre la selección.
 *
 * En una sola fila que se parte en varias en pantalla estrecha. El orden no
 * es casual: primero lo que se usa siempre (buscar), luego lo ocasional
 * (filtros, columnas) y al final las acciones, pegadas a la derecha.
 */
export function DataTableToolbar<TData>({
  table,
  search,
  filters,
  bulkActions,
  hideableColumns,
  actions,
}: {
  table: Table<TData>;
  search?: { placeholder?: string; value: string; onChange: (value: string) => void };
  filters: DataTableFilter[];
  bulkActions: DataTableBulkAction<TData>[];
  hideableColumns: boolean;
  actions?: ReactNode;
}) {
  const seleccionadas = table.getSelectedRowModel().rows;
  const hayFiltros = search !== undefined || filters.length > 0 || hideableColumns;

  if (!hayFiltros && actions === undefined && bulkActions.length === 0) return null;

  return (
    <div className="mb-3 flex flex-wrap items-center gap-2">
      {search && (
        <label className="relative min-w-[12rem] flex-1 sm:max-w-xs">
          <span className="sr-only">Buscar en la tabla</span>
          <span aria-hidden className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-500">
            <Icono nombre="buscar" className="h-4 w-4" />
          </span>
          <input
            type="search"
            value={search.value}
            onChange={(e) => search.onChange(e.target.value)}
            placeholder={search.placeholder ?? "Buscar…"}
            className="w-full rounded-lg border border-white/10 bg-white/[0.04] py-1.5 pl-8 pr-3 text-sm text-slate-100 placeholder:text-slate-600 transition focus:border-sky-400/60 focus:bg-white/[0.07] focus:outline-none"
          />
        </label>
      )}

      <DataTableFilters table={table} filters={filters} />

      {hideableColumns && <SelectorDeColumnas table={table} />}

      {/* Las acciones masivas aparecen **solo con algo seleccionado**: un
          botón «Eliminar 0 elementos» siempre visible es ruido, y uno
          habilitado sin selección es una trampa. */}
      {seleccionadas.length > 0 && bulkActions.length > 0 && (
        <div className="flex items-center gap-2 rounded-lg border border-sky-400/30 bg-sky-500/10 px-2.5 py-1">
          <span className="text-xs text-sky-100">
            {seleccionadas.length} seleccionad{seleccionadas.length === 1 ? "o" : "os"}
          </span>
          {bulkActions.map((accion) => (
            <button
              key={accion.label}
              type="button"
              onClick={() => accion.onSelect(seleccionadas.map((r) => r.original))}
              className={`rounded-md px-2 py-0.5 text-xs transition ${
                accion.tone === "danger"
                  ? "text-rose-200 hover:bg-rose-500/20"
                  : "text-slate-200 hover:bg-white/[0.09]"
              }`}
            >
              {accion.label}
            </button>
          ))}
        </div>
      )}

      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
    </div>
  );
}

/**
 * Selector de columnas visibles.
 *
 * Útil en tablas anchas —llamadas, consumo— donde cada quien mira dos o tres
 * columnas distintas. Lo que se oculta aquí **no** se guarda entre sesiones a
 * propósito: una columna que desapareció hace tres semanas y nadie recuerda
 * haber ocultado se denuncia como dato que falta.
 */
function SelectorDeColumnas<TData>({ table }: { table: Table<TData> }) {
  const [abierto, setAbierto] = useState(false);
  const contenedor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!abierto) return;
    function fuera(event: MouseEvent) {
      if (!contenedor.current?.contains(event.target as Node)) setAbierto(false);
    }
    document.addEventListener("mousedown", fuera);
    return () => document.removeEventListener("mousedown", fuera);
  }, [abierto]);

  const ocultables = table.getAllLeafColumns().filter((c) => c.getCanHide());
  if (ocultables.length === 0) return null;

  const ocultas = ocultables.filter((c) => !c.getIsVisible()).length;

  return (
    <div ref={contenedor} className="relative">
      <button
        type="button"
        aria-haspopup="true"
        aria-expanded={abierto}
        onClick={() => setAbierto((v) => !v)}
        className="flex items-center gap-1.5 rounded-lg border border-white/15 bg-white/[0.04] px-2.5 py-1.5 text-xs text-slate-300 transition hover:bg-white/[0.09] hover:text-white"
      >
        <Icono nombre="columnas" className="h-3.5 w-3.5" />
        Columnas
        {ocultas > 0 && <span className="text-sky-300">({ocultas} ocultas)</span>}
      </button>

      {abierto && (
        <div className="absolute left-0 z-20 mt-1 min-w-[12rem] rounded-lg border border-white/10 bg-[#11162a] py-1 shadow-xl">
          {ocultables.map((column) => (
            <label
              key={column.id}
              className="flex cursor-pointer items-center gap-2 px-3 py-1.5 text-sm text-slate-200 transition hover:bg-white/[0.07]"
            >
              <input
                type="checkbox"
                checked={column.getIsVisible()}
                onChange={(e) => column.toggleVisibility(e.target.checked)}
              />
              {column.columnDef.meta?.label ?? column.id}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
